import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Play, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import client from "../lib/api";
import { GRADE_HEX, num } from "../lib/format";

const GRADE_KEY = { "A+": "grade_A", Strong: "grade_Strong", Watch: "grade_Watch", Weak: "grade_Weak", Avoid: "grade_Avoid" };

export default function Backtest() {
  const [sp] = useSearchParams();
  const [ticker, setTicker] = useState(sp.get("ticker") || "");
  const [direction, setDirection] = useState(sp.get("direction") || "long");
  const [hold, setHold] = useState(10);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTicker = async (t) => {
    if (!t) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await client.get(`/backtest/ticker/${t}`, { params: { direction, hold } });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backtest failed");
    } finally { setLoading(false); }
  };

  const runUniverse = async () => {
    setLoading(true); setResult(null);
    toast("Replaying rules across the full universe — this takes ~30-60s");
    try {
      const { data } = await client.post("/backtest/run", null, { params: { direction, hold } });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backtest failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (sp.get("ticker")) runTicker(sp.get("ticker"));
    else client.get("/backtest/universe", { params: { direction, hold } })
      .then(({ data }) => { if (data.ready) setResult(data); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const chartData = result?.by_grade?.filter((g) => g.trades > 0).map((g) => ({
    grade: g.grade, win_rate: g.win_rate, expectancy: g.expectancy, trades: g.trades, key: GRADE_KEY[g.grade],
  })) || [];

  return (
    <div className="px-4 py-5 sm:px-6">
      <div>
        <h1 className="font-head text-2xl font-black tracking-tight" data-testid="backtest-title">BACKTEST</h1>
        <p className="mt-1 text-sm text-gray-500">Replays the exact 7-gate rules on 2y of daily history. Turns "quality" into calibrated <span className="text-white">win rate · avg R · expectancy</span> by grade.</p>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input data-testid="bt-ticker-input" value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
          placeholder="TICKER (e.g. NVDA)" onKeyDown={(e) => e.key === "Enter" && runTicker(ticker)}
          className="w-40 rounded-sm border border-white/10 bg-transparent px-3 py-1.5 font-mono-sg text-sm text-white outline-none focus:border-[#00F0FF]" />
        <button data-testid="bt-run-ticker" onClick={() => runTicker(ticker)}
          className="flex items-center gap-1.5 rounded-sm border border-white/10 px-3 py-1.5 font-mono-sg text-xs text-gray-300 hover:border-white/20 hover:text-white">
          <Play size={13} /> Run ticker
        </button>
        <span className="mx-1 h-4 w-px bg-white/10" />
        <div className="flex overflow-hidden rounded-sm border border-white/10">
          <button data-testid="bt-long" onClick={() => setDirection("long")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "long" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingUp size={13} />LONG</button>
          <button data-testid="bt-short" onClick={() => setDirection("short")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "short" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingDown size={13} />SHORT</button>
        </div>
        <select data-testid="bt-hold" value={hold} onChange={(e) => setHold(Number(e.target.value))}
          className="rounded-sm border border-white/10 bg-[#111111] px-2 py-1.5 font-mono-sg text-xs text-gray-300 outline-none">
          {[3, 5, 10].map((h) => <option key={h} value={h}>hold {h}d</option>)}
        </select>
        <button data-testid="bt-run-universe" onClick={runUniverse}
          className="flex items-center gap-1.5 rounded-sm bg-[#00F0FF] px-3 py-1.5 font-mono-sg text-xs font-bold text-black hover:bg-[#00F0FF]/80">
          <Play size={13} /> Run full universe
        </button>
      </div>

      {loading && (
        <div className="mt-6 flex flex-col items-center justify-center gap-3 border border-white/10 py-24">
          <Loader2 size={26} className="animate-spin text-[#00F0FF]" />
          <p className="font-mono-sg text-sm text-gray-400 sg-blink">REPLAYING HISTORICAL RULES…</p>
        </div>
      )}

      {result && !loading && (
        <div className="mt-6 space-y-4 sg-fade">
          <div className="flex flex-wrap items-center gap-3 border border-white/10 bg-[#111111] p-4">
            <span className="font-head text-lg font-bold">{result.ticker ? result.ticker : "UNIVERSE"}</span>
            <span className="font-mono-sg text-xs text-gray-500">{result.direction?.toUpperCase()} · hold {result.hold}d</span>
            <div className="ml-auto flex gap-6 font-mono-sg text-sm">
              <Metric label="Trades" value={result.overall.trades} />
              <Metric label="Win rate" value={`${num(result.overall.win_rate, 1)}%`} color={result.overall.win_rate >= 50 ? "#00FF66" : "#FF7F00"} />
              <Metric label="Avg R" value={num(result.overall.avg_r, 2)} color={result.overall.avg_r > 0 ? "#00FF66" : "#FF3B30"} />
              <Metric label="Expectancy" value={num(result.overall.expectancy, 2) + "R"} color={result.overall.expectancy > 0 ? "#00FF66" : "#FF3B30"} />
            </div>
          </div>

          {result.overall.trades === 0 ? (
            <div className="border border-white/10 py-16 text-center text-sm text-gray-500">No qualifying setups in the lookback window.</div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="border border-white/10 bg-[#111111] p-5">
                <h2 className="font-head text-lg font-bold">Win Rate by Grade</h2>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid stroke="#1a1a1a" vertical={false} />
                      <XAxis dataKey="grade" stroke="#444" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                      <YAxis stroke="#444" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} unit="%" />
                      <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                      <Bar dataKey="win_rate" name="Win %">
                        {chartData.map((e, i) => <Cell key={i} fill={GRADE_HEX[e.key]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border border-white/10 bg-[#111111] p-5">
                <h2 className="font-head text-lg font-bold">Expectancy (R) by Grade</h2>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid stroke="#1a1a1a" vertical={false} />
                      <XAxis dataKey="grade" stroke="#444" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                      <YAxis stroke="#444" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                      <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                      <Bar dataKey="expectancy" name="Expectancy R">
                        {chartData.map((e, i) => <Cell key={i} fill={e.expectancy >= 0 ? GRADE_HEX[e.key] : "#FF3B30"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {result.overall.trades > 0 && (
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full" data-testid="backtest-grade-table">
                <thead className="bg-[#111111]">
                  <tr className="text-left font-mono-sg text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 py-2.5">Grade</th><th className="px-3 py-2.5 text-right">Trades</th>
                    <th className="px-3 py-2.5 text-right">Win %</th><th className="px-3 py-2.5 text-right">Avg R</th>
                    <th className="px-3 py-2.5 text-right">Expectancy</th><th className="px-3 py-2.5 text-right">Avg Win</th>
                    <th className="px-3 py-2.5 text-right">Avg Loss</th>
                  </tr>
                </thead>
                <tbody className="font-mono-sg text-sm">
                  {result.by_grade.map((g) => (
                    <tr key={g.grade} className="border-t border-white/5">
                      <td className="px-3 py-2.5 font-bold" style={{ color: GRADE_HEX[GRADE_KEY[g.grade]] }}>{g.grade}</td>
                      <td className="px-3 py-2.5 text-right text-gray-300">{g.trades}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: g.win_rate >= 50 ? "#00FF66" : "#FF7F00" }}>{g.win_rate === null ? "—" : `${g.win_rate}%`}</td>
                      <td className="px-3 py-2.5 text-right text-gray-300">{g.avg_r ?? "—"}</td>
                      <td className="px-3 py-2.5 text-right" style={{ color: (g.expectancy || 0) > 0 ? "#00FF66" : "#FF3B30" }}>{g.expectancy ?? "—"}</td>
                      <td className="px-3 py-2.5 text-right text-[#00FF66]">{g.avg_win || "—"}</td>
                      <td className="px-3 py-2.5 text-right text-[#FF3B30]">{g.avg_loss || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-600">{label}</div>
      <div className="font-bold" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}
